import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import ElasticService from "src/services/elasticService";
import { makeStyles } from "tss-react/mui";

import { Box, Theme, Typography } from "@mui/material";
import { FilterBadge } from "@shared/CustomBadge";
import { PrimaryButton } from "@shared/CustomButton";
import { CheckboxField, ControlledCheckboxField } from "@shared/form/CheckboxField";
import { Modal } from "@shared/Modal/Modal";

const useStyles = makeStyles()((theme: Theme) => ({
    content: { minHeight: 300, background: theme.palette.emteria.background.paper, borderRadius: "6px" },
    empty: { height: 150, display: "flex", justifyContent: "center", alignItems: "center" },
    informationText: {
        fontWeight: 600,
    },
    checkboxContainer: {
        display: "flex",
        flexDirection: "column",
        marginLeft: theme.spacing(4),
        padding: theme.spacing(3, 0),
        maxHeight: 300,
        overflow: "auto",
        overflowX: "hidden",
    },
    selectManyContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
}));

const changeAllValuesTo = (obj: Record<string, boolean>, value: boolean) => {
    const copy = { ...obj };
    for (const key of Object.keys(copy)) {
        copy[key] = value;
    }
    return copy;
};

interface FormValues {
    [x: string]: boolean;
}

type TagFilterProps = {
    tags: API.Tag[];
    filters: string[];
    onChange: (values: string[]) => void;
};
export const TagFilter = (props: TagFilterProps) => {
    const [open, setIsOpen] = React.useState(false);
    const { classes } = useStyles();
    const location = useLocation();
    const { t } = useTranslation(["modals", "common"]);

    const filters = _.mapValues(_.keyBy(props.tags, "key"), (item) => props.filters.includes(item.key));
    const filtersArr = Object.keys(filters);
    const truthyFilters = changeAllValuesTo(filters, true);
    const falsyFilters = changeAllValuesTo(filters, false);

    const onSubmit = (values: FormValues) => {
        const value = Object.keys(values).filter((key) => values[key]);
        props.onChange(value);
        setIsOpen(false);
    };

    const handleCloseButton = (form: FormApi<FormValues, Partial<FormValues>>) => {
        form.reset(filters);
        setIsOpen(false);
    };

    const onOpen = () => {
        setIsOpen(true);
        ElasticService.createTransaction("tagFilter", {
            page: location.pathname,
        });
    };

    return (
        <React.Fragment>
            <FilterBadge
                badgeContent={Object.values(filters).filter((item) => item).length}
                onClick={onOpen}
                data-testid="tag-filter-button"
            />
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={filters}
                render={({ form, handleSubmit, values }) => {
                    return (
                        <Modal
                            open={open}
                            title={t("modals:tagFilter.title")}
                            onClose={() => handleCloseButton(form)}
                            endButton={<PrimaryButton onClick={form.submit}>{t("common:button.save")}</PrimaryButton>}
                        >
                            <form onSubmit={handleSubmit}>
                                <div className={classes.content}>
                                    {!filtersArr.length ? (
                                        <Box className={classes.empty}>
                                            <Typography>{t("modals:tagFilter.noTags")}</Typography>
                                        </Box>
                                    ) : null}
                                    <div className={classes.checkboxContainer}>
                                        {filtersArr.map((item) => (
                                            <ControlledCheckboxField key={item} name={item} label={item} />
                                        ))}
                                    </div>
                                </div>
                                <div className={classes.selectManyContainer}>
                                    {values && Object.values(values).length ? (
                                        <CheckboxField
                                            label={t("common:button.selectAll")}
                                            checked={Object.values(values).every((item) => item)}
                                            indeterminate={false}
                                            onChange={(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                                form.reset(checked ? truthyFilters : falsyFilters);
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </form>
                        </Modal>
                    );
                }}
            />
        </React.Fragment>
    );
};
